import { Sparkles } from 'lucide-react';
import { useRef, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { api } from '@/lib/api';
import { applyTeachingPlanToStore, summarizeSlideElements } from '@/lib/apply-teaching-plan';
import type { Slide, TeachingPlanStep } from '@/lib/schema.types';
import { useEditorActions } from '@/store/editor.store';

interface Props {
  open: boolean;
  onClose: () => void;
  slide: Slide;
  slideId: string;
}

type Phase = 'idle' | 'running' | 'preview' | 'error';

/**
 * One-click AI teaching: asks the backend to plan the reveal order + spoken
 * narration text + emphasis cues for this slide, previews it, and applies it to
 * the slide's reveal steps. AUDIO-FREE — narration audio is generated afterwards
 * via the existing VoxCPM flow.
 */
export function AiTeachDialog({ open, onClose, slide, slideId }: Props) {
  const actions = useEditorActions();
  const [persona, setPersona] = useState('');
  const [tone, setTone] = useState('warm, exam-focused');
  const [language, setLanguage] = useState('English');
  const [socratic, setSocratic] = useState(false);
  const [targetSeconds, setTargetSeconds] = useState(12);
  const [userHint, setUserHint] = useState('');

  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const [plan, setPlan] = useState<TeachingPlanStep[]>([]);
  const abortRef = useRef<AbortController | null>(null);

  const elements = summarizeSlideElements(slide);

  const reset = () => {
    setPhase('idle');
    setPlan([]);
    setError('');
    setProgress('');
  };

  const run = async () => {
    if (!elements.length) {
      setError('This slide has no elements to teach.');
      setPhase('error');
      return;
    }
    setPhase('running');
    setProgress('Planning the lesson…');
    setError('');
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      for await (const ev of api.ai.generateTeachingStream(
        {
          slideLabel: slide.label,
          slideId,
          elements,
          options: {
            persona: persona.trim() || undefined,
            tone: tone.trim() || undefined,
            language: language.trim() || undefined,
            socratic,
            targetSeconds,
            userHint: userHint.trim() || undefined,
          },
        },
        { signal: ctrl.signal },
      )) {
        if (ev.type === 'plan') {
          setPlan(ev.steps);
          setPhase('preview');
        } else if (ev.type === 'warning') {
          setProgress(ev.message);
        } else if (ev.type === 'error') {
          setError(ev.message);
          setPhase('error');
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Generation failed.');
      setPhase('error');
    } finally {
      abortRef.current = null;
    }
  };

  const apply = () => {
    applyTeachingPlanToStore({
      slideId,
      existingStepIds: (slide.revealSteps ?? []).map((s) => s.id),
      steps: plan,
      actions,
    });
    reset();
    onClose();
  };

  const elementLabel = (id: string) => elements.find((e) => e.id === id)?.summary ?? id;

  return (
    <Modal
      open={open}
      onClose={() => {
        abortRef.current?.abort();
        reset();
        onClose();
      }}
      title="AI Teach"
      description="Plan the reveal order, narration, and emphasis for this slide. Audio is generated afterwards."
      width="lg"
      footer={
        phase === 'preview' ? (
          <>
            <Button variant="ghost" onClick={reset}>
              Discard
            </Button>
            <Button variant="secondary" onClick={run}>
              Regenerate
            </Button>
            <Button variant="primary" onClick={apply}>
              Apply {plan.length} steps
            </Button>
          </>
        ) : (
          <Button variant="primary" onClick={run} disabled={phase === 'running'}>
            <Sparkles size={14} /> {phase === 'running' ? 'Generating…' : 'Generate plan'}
          </Button>
        )
      }
    >
      {phase === 'preview' ? (
        <ol className="space-y-2">
          {plan.map((s, i) => (
            <li key={`${s.elementId}-${i}`} className="rounded-md border border-border p-2.5">
              <div className="mb-1 flex items-center gap-2 text-[11px] text-fg-subtle">
                <span className="grid h-5 w-5 place-items-center rounded-full bg-accent-soft text-[10px] font-bold text-accent">
                  {i + 1}
                </span>
                <span className="line-clamp-1">{elementLabel(s.elementId)}</span>
                {s.takeInput && (
                  <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold text-amber-700">
                    WAITS FOR INPUT
                  </span>
                )}
              </div>
              <p className="text-sm text-fg">{s.speechPrompt || <em className="text-fg-subtle">No narration</em>}</p>
              {s.cues.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {s.cues.map((c, ci) => (
                    <span key={ci} className="rounded bg-bg-subtle px-1.5 py-0.5 text-[10px] text-fg-muted">
                      {c.kind}
                      {c.textSnippet ? `: "${c.textSnippet}"` : ''}
                    </span>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ol>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input label="Persona (optional)" value={persona} onChange={(e) => setPersona(e.target.value)} placeholder="e.g. warm Indian tutor" />
            <Input label="Tone" value={tone} onChange={(e) => setTone(e.target.value)} />
            <Input label="Language" value={language} onChange={(e) => setLanguage(e.target.value)} />
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium text-fg-muted">Seconds / step</span>
              <input
                type="number"
                min={2}
                max={120}
                value={targetSeconds}
                onChange={(e) => setTargetSeconds(Number(e.target.value) || 12)}
                className="h-9 w-full rounded-md border border-border bg-white px-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
              />
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm text-fg">
            <input type="checkbox" checked={socratic} onChange={(e) => setSocratic(e.target.checked)} />
            Socratic mode (guide with questions first)
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] font-medium text-fg-muted">Direction (optional)</span>
            <textarea
              value={userHint}
              onChange={(e) => setUserHint(e.target.value)}
              rows={2}
              placeholder="Anything specific the tutor should emphasise…"
              className="w-full rounded-md border border-border bg-white p-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
            />
          </label>
          <p className="text-[11px] text-fg-subtle">
            Applying replaces this slide&apos;s {(slide.revealSteps ?? []).length} reveal step(s)
            with the AI plan. {elements.length} element(s) on this slide.
          </p>
          {phase === 'running' && <p className="text-[12px] text-accent">{progress}</p>}
          {phase === 'error' && <p className="text-[12px] text-red-600">{error}</p>}
        </div>
      )}
    </Modal>
  );
}
